import { useState, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import OfferCard from './components/OfferCard';
import CategoryFilter from './components/CategoryFilter';
import OfferDetail from './components/OfferDetail';
import HowItWorks from './components/HowItWorks';
import PageTransition from './components/PageTransition';
import { OffersResponse, Offer } from './types/offers';
import { getOffers, getLocalOffers } from './api/offers';
import { closeWebView, trackOfferViewed } from './utils/nativeBridge';
import { useSegmentTracking, EVENTS } from './hooks/useSegmentTracking';
import { sentry } from './services/sentry';
import { BoundaryFallbackUI } from './services/sentry/BoundaryFallbackUI';

function Home() {
  const { track, identify } = useSegmentTracking();
  const [selectedCategory, setSelectedCategory] = useState<string | null>('All categories');
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [isDetailActive, setIsDetailActive] = useState(false);
  const [isHowItWorksActive, setIsHowItWorksActive] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android'>('ios');
  const [offers, setOffers] = useState<OffersResponse>({
    data: [],
    links: {
      self: '',
      prev: null,
      next: null
    },
    included: []
  });
  const [localOffers, setLocalOffers] = useState<OffersResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Effect to get platform and identify user from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const platformParam = urlParams.get('platform')?.toLowerCase();
    if (platformParam === 'android' || platformParam === 'ios') {
      setPlatform(platformParam);
    }
    const publicToken = urlParams.get('public_token');
    if (publicToken) {
      identify(publicToken);
    }
  }, [identify]);

  useEffect(() => {
    if (isDetailActive) return;
    const path = isHowItWorksActive ? '/how-it-works' : '/';
    track(EVENTS.PageViewed, { hostname: window.location.hostname, path, search: '' });
  }, [isDetailActive, isHowItWorksActive, track]);

  // Effect to prevent background scrolling when modals/detail pages are active
  useEffect(() => {
    const body = document.body;
    if (isDetailActive || isHowItWorksActive) {
      // Add class to prevent scrolling
      body.classList.add('overflow-hidden');
    } else {
      // Remove class when going back to main page
      body.classList.remove('overflow-hidden');
    }

    // Cleanup when component unmounts
    return () => {
      body.classList.remove('overflow-hidden');
    };
  }, [isDetailActive, isHowItWorksActive]);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const nationalOffersPromise = getOffers();
        const localOffersPromise = getLocalOffers();

        const [nationalData, localData] = await Promise.all([
          nationalOffersPromise,
          localOffersPromise.catch((err) => {
            console.error('Error fetching local offers (failing silently):', err);
            sentry.captureError(err, { context: { requestURL: '/api/creditcard/kard/localOffers' } });
            return null;
          })
        ]);

        console.log('Fetched offers data:', {
          hasNationalData: !!nationalData,
          nationalDataLength: nationalData?.data?.length,
          hasLocalData: !!localData,
          localDataLength: localData?.data?.length,
        });

        if (!nationalData || !Array.isArray(nationalData.data)) {
          throw new Error('Invalid offers data received');
        }

        setOffers(nationalData);
        setLocalOffers(localData);
      } catch (err) {
        console.error('Error fetching offers:', err);
        setError('Failed to load offers.');
        sentry.captureError(err, { context: { requestURL: '/api/creditcard/kard/offers' } });
        track(EVENTS.Error, {
          displayMessage: err instanceof Error ? err.message : 'Failed to load offers',
          requestURL: '/api/creditcard/kard/offers',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const showNearbyCategory = useMemo(() => {
    if (!localOffers || localOffers.data.length === 0) return false;

    return true;
  }, [localOffers]);

  // Merge categories from both national and local offers
  const mergedCategories = useMemo(() => {
    if (!localOffers || localOffers.included.length === 0) {
      return offers.included;
    }

    // Create a map to deduplicate categories by ID
    const categoriesMap = new Map();

    // Add national offer categories
    offers.included.forEach(category => {
      categoriesMap.set(category.id, category);
    });

    // Add local offer categories (will overwrite if same ID exists)
    localOffers.included.forEach(category => {
      categoriesMap.set(category.id, category);
    });

    return Array.from(categoriesMap.values());
  }, [offers.included, localOffers]);

  // V1: Show all offers (local + national) without deduplication
  const mergedOffers = useMemo(() => {
    if (!localOffers || localOffers.data.length === 0) {
      return offers.data;
    }
    return [...localOffers.data, ...offers.data];
  }, [offers.data, localOffers]);

  const filteredOffers = (mergedOffers || []).filter(offer => {
    if (!offer || !offer.attributes) {
      console.warn('Invalid offer found:', offer);
      return false;
    }

    // Filter by "Nearby" category
    if (selectedCategory === 'Nearby') {
      return offer.attributes.is_local === true;
    }

    // No filtering when "All categories" is selected
    if (selectedCategory === 'All categories') {
      return true;
    }

    // Only filter by category if one is selected
    if (!selectedCategory) {
      return true;
    }

    // Check if offer has relationships and category data before filtering
    if (!offer.relationships || !offer.relationships.category || !offer.relationships.category.data) {
      console.warn('Offer missing relationships.category.data:', offer.attributes.name);
      return false;
    }

    // Check if any of the offer's categories match the selected category
    return offer.relationships.category.data.some(cat => {
      // Search in both national and local offers' included categories
      let categoryInfo = offers.included.find(inc => inc.id === cat.id);
      if (!categoryInfo && localOffers) {
        categoryInfo = localOffers.included.find(inc => inc.id === cat.id);
      }
      return categoryInfo?.attributes.name === selectedCategory;
    });
  });

  const handleOfferClick = (offer: Offer) => {
    setSelectedOffer(offer);
    setIsDetailActive(true);
    trackOfferViewed(offer);
    track(EVENTS.PageViewed, {
      hostname: window.location.hostname,
      path: '/offer-detail',
      search: '',
      offerName: offer.attributes.name,
      offerRewardType: offer.attributes.user_reward.type,
      offerRewardValue: offer.attributes.user_reward.value,
    });
  };

  const handleBackClick = () => {
    if (isDetailActive) {
      setIsDetailActive(false);
      // Clear the selected offer only after the animation completes
      setTimeout(() => {
        setSelectedOffer(null);
      }, 300);
    } else if (isHowItWorksActive) {
      setIsHowItWorksActive(false);
    }
  };

  const handleHowItWorksClick = () => {
    setIsHowItWorksActive(true);
    track(EVENTS.ButtonClicked, { id: 'how-it-works-btn' });
  };

  // Handle close button click - for iOS webview detection
  const handleCloseClick = () => {
    closeWebView();
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Main page - Offers List */}
      <div className={isDetailActive || isHowItWorksActive ? 'hidden' : ''}>
        {/* Sticky Header with title and close button */}
        <div className="sticky top-0 z-10 bg-white px-4 py-4 flex items-center">
          <button className="p-1" onClick={handleCloseClick} aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h1 className="label-medium absolute left-1/2 transform -translate-x-1/2">Offers</h1>
          <button
            className="paragraph-medium text-gray-700 hover:text-gray-900 ml-auto"
            onClick={handleHowItWorksClick}
          >
            How it works
          </button>
        </div>

        {/* Category Filter - Horizontal scrollable */}
        <div className="py-4">
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategorySelect={setSelectedCategory}
            categories={mergedCategories}
            platform={platform}
            showNearby={showNearbyCategory}
          />
        </div>

        {/* Offers List Section */}
        <div className="px-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center min-h-[200px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <p className="text-yellow-700">{error}</p>
            </div>
          )}

          {/* Offers List */}
          {!isLoading && !error && filteredOffers.length > 0 && (
            <div className="space-y-0">
              {filteredOffers.map((offer) => (
                <OfferCard
                  key={offer.attributes.is_local && offer.attributes.locationId
                    ? `${offer.id}-${offer.attributes.locationId}`
                    : offer.id}
                  offer={offer}
                  onClick={() => handleOfferClick(offer)}
                />
              ))}
            </div>
          )}

          {/* No Results Message */}
          {!isLoading && !error && filteredOffers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No offers found matching your criteria
            </div>
          )}
        </div>
      </div>

      {/* Offer Detail page with transition */}
      {selectedOffer && isDetailActive && (
        <PageTransition isActive={isDetailActive} direction="right-to-left">
          <OfferDetail offer={selectedOffer} onBack={handleBackClick} />
        </PageTransition>
      )}

      {/* How It Works page with transition */}
      {isHowItWorksActive && (
        <PageTransition isActive={isHowItWorksActive} direction="right-to-left">
          <HowItWorks onBack={handleBackClick} />
        </PageTransition>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={BoundaryFallbackUI}>
      <Home />
    </Sentry.ErrorBoundary>
  );
}
